import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ThreeDotMenu, ThreeDotMenuItem } from '@mm/components';
import { CopyIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import ConnectionModel, {
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Spin } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFetchConnections } from '../../../beta/modules/Integrations/use-fetch-connections';
import { ConnectionsPanel } from '../../panels/ConnectionsPanel/ConnectionsPanel';
import './ConnectionsTableGrid.scss';

const renderNameCell = (
  item: FormattedConnectionModel,
  onEditConnection: (item: FormattedConnectionModel) => void,
  nameToOverride?: string,
) => (
  <div
    className="connection-table__clickable-cell"
    onClick={() => onEditConnection(item)}
  >
    {nameToOverride || item.name}
  </div>
);

const renderActionsCell = (
  item: FormattedConnectionModel,
  onEditConnection: (id: FormattedConnectionModel) => void,
  onDeleteConnection: (item: FormattedConnectionModel) => void,
  onCloneConnection: (item: FormattedConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return onDeleteConnection(item);
      case 'clone':
        return onCloneConnection(item);
      default:
        break;
    }
  };

  const elements: ThreeDotMenuItem[] = [
    {
      name: 'Clone',
      value: 'clone',
      children: (
        <div className="connection-table__action-cell-item">
          <div className="connection-table__action-cell-item_icon">
            <CopyIcon color="#262626" size={14} />
          </div>
          <div>Clone</div>
        </div>
      ),
      hideName: true,
    },
    {
      name: 'Delete',
      value: 'delete',
      children: (
        <div className="connection-table__action-cell-item">
          <div className="connection-table__action-cell-item_icon">
            <DeleteOutlined />
          </div>
          <div>Delete</div>
        </div>
      ),
      hideName: true,
    },
  ];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="connection-table__action-cell">
      <EditOutlined
        onClick={() => onEditConnection(item)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </div>
  );
};

const generateTableCells = (
  onEditConnection: (item: FormattedConnectionModel) => void,
  onDeleteConnection: (item: FormattedConnectionModel) => void,
  onCloneConnection: (item: FormattedConnectionModel) => void,
) => {
  const cells = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.name.localeCompare(b.name),
      width: '25%',
      classNames: 'connection-table__clickable-cell',
      render: (text, record: FormattedConnectionModel) =>
        renderNameCell(record, onEditConnection),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.description.localeCompare(b.description),

      classNames: 'connection-table__clickable-cell',
      render: (text, record: FormattedConnectionModel) =>
        renderNameCell(record, onEditConnection, 'description'),
      width: '30%',
    },
    {
      title: 'Leads sent',
      dataIndex: 'contactsSent',
      key: 'contactsSent',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        (a.contactsSent || 0) - (b.contactsSent || 0),
      width: '10%',
    },
    {
      title: 'Created',
      dataIndex: 'formattedCreatedAt',
      key: 'formattedCreatedAt',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        new Date(a.formattedCreatedAt).getTime() -
        new Date(b.formattedCreatedAt).getTime(),
      width: '10%',
    },
    {
      title: 'Updated',
      dataIndex: 'formattedUpdatedAt',
      key: 'formattedUpdatedAt',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        new Date(a.formattedUpdatedAt).getTime() -
        new Date(b.formattedUpdatedAt).getTime(),
      width: '10%',
    },
    {
      title: 'Status',
      dataIndex: 'statusText',
      key: 'statusText',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.statusText.localeCompare(b.statusText),
      width: '10%',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record: FormattedConnectionModel) =>
        renderActionsCell(
          record,
          onEditConnection,
          onDeleteConnection,
          onCloneConnection,
        ),
      width: '5%',
    },
  ];
  return cells;
};

const ConnectionsTableGrid = () => {
  const connectionsCRUDActions = useConnectionCRUDActions();
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();
  const { fetch, isFetching } = useFetchConnections();

  useEffect(() => {
    if (connectedPageId) {
      fetch(1, 200, undefined);
    }
  }, [fetch, connectedPageId]);

  const onDeleteConnection = (connection: ConnectionModel) => {
    const deleteCallback = async () => {
      connectionsCRUDActions.deleteConnection(connectedPageId, connection);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${connection.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onEditConnection = (connection: ConnectionModel) => {
    showSlideoutMenu('editJSONWebhookIntegrationEditor', {
      webhookId: connection.id,
    });
  };

  const onCloneConnection = (connection: ConnectionModel) => {
    const cloneAction = async () => {
      const newConnection = await connectionsCRUDActions.clone(
        connectedPageId,
        connection.id,
      );

      if (newConnection) {
        showSlideoutMenu('editJSONWebhookIntegrationEditor', {
          webhookId: newConnection.id,
        });
      }
    };

    cloneAction();
  };

  const data = generateTableCells(
    onEditConnection,
    onDeleteConnection,
    onCloneConnection,
  );

  return isFetching ? <></> : <ConnectionsPanel cellData={data} />;
};

export default ConnectionsTableGrid;
